import React, { useEffect, useRef, useState } from 'react';
import './Table.css';

const Table = ({
  columns,
  data,
  renderRow,
  className = '',
  onRowClick,
  onSort,
  sortConfig
}) => {
  const headerRef = useRef(null);
  const bodyRef = useRef(null);

  // State for sorting
  const [internalSortConfig, setInternalSortConfig] = useState({ key: null, direction: 'ascending' });

  // Use controlled sortConfig if provided, else internal state
  const effectiveSortConfig = sortConfig || internalSortConfig;

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Function to handle sorting
  const sortedData = React.useMemo(() => {
    let sortableItems = [...data];
    if (effectiveSortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[effectiveSortConfig.key] < b[effectiveSortConfig.key]) {
          return effectiveSortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[effectiveSortConfig.key] > b[effectiveSortConfig.key]) {
          return effectiveSortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [data, effectiveSortConfig]);

  // Calculate paginated data
  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage]);

  const requestSort = (key) => {
    if (onSort) {
      onSort(key);
    } else {
      let direction = 'ascending';
      if (internalSortConfig.key === key && internalSortConfig.direction === 'ascending') {
        direction = 'descending';
      }
      setInternalSortConfig({ key, direction });
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  // Handle page change
  const goToPage = (page) => {
    if (page < 1) page = 1;
    else if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1); // Reset to first page
  };

  useEffect(() => {
    const syncColumnWidths = () => {
      if (headerRef.current && bodyRef.current) {
        const headerCells = headerRef.current.querySelectorAll('th');
        const firstBodyRow = bodyRef.current.querySelector('tbody tr');

        if (firstBodyRow) {
          const bodyCells = firstBodyRow.querySelectorAll('td');

          headerCells.forEach((th, index) => {
            if (bodyCells[index]) {
              // const minWidth = th.classList.contains('actions-header') ? 120 : 100;
              // const calculatedWidth = Math.max(bodyCells[index].offsetWidth, minWidth);
              // th.style.width = `${calculatedWidth}px`;
              // bodyCells[index].style.width = `${calculatedWidth}px`;
                          // Check if column has custom style (maxWidth or width)
              const columnStyle = columns[index]?.style;
              const hasCustomWidth = columnStyle && (columnStyle.maxWidth || columnStyle.width);

              if (hasCustomWidth) {
                // Use the custom style's maxWidth (convert '50px' to 50)
                const customWidth = parseInt(columnStyle.maxWidth || columnStyle.width, 10);
                th.style.width = `${customWidth}px`;
                th.style.maxWidth = columnStyle.maxWidth || columnStyle.width;
                bodyCells[index].style.width = `${customWidth}px`;
                bodyCells[index].style.maxWidth = columnStyle.maxWidth || columnStyle.width;
              } else {
                // Use dynamic calculation for columns without custom style
                const minWidth = th.classList.contains('actions-header') ? 120 : 100;
                const calculatedWidth = Math.max(bodyCells[index].offsetWidth, minWidth);
                th.style.width = `${calculatedWidth}px`;
                bodyCells[index].style.width = `${calculatedWidth}px`;
              }
            }
          });
        }
      }
    };

    syncColumnWidths();
    window.addEventListener('resize', syncColumnWidths);

    return () => {
      window.removeEventListener('resize', syncColumnWidths);
    };
  }, [paginatedData, columns]);

  // Generate page numbers for pagination controls with ellipsis
  const getPageNumbers = () => {
    const maxPageButtons = 5;
    let pages = [];

    if (totalPages <= maxPageButtons) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages = [1, 2, 3, 4, 5];
      } else if (currentPage >= totalPages - 2) {
        pages = [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        pages = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
      }
    }
    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className="table-container">
      <div className="rows-per-page-container">
        <label>
          Rows per page:{' '}
          <select value={rowsPerPage} onChange={handleRowsPerPageChange} className="rows-per-page-select">
            {[5, 10, 25, 50].map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="sticky-header-wrapper" ref={headerRef}>
        <table className={`universal-table ${className}`}>
          <thead>
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className={col.className ? col.className : ''}
                  title={col.title ? col.title : ''}
                  onClick={() => requestSort(col.key)} // Add click handler for sorting
                >
                  {col.header} {effectiveSortConfig.key === col.key ? (effectiveSortConfig.direction === 'ascending' ? '🔼' : '🔽') : ''}
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>
      <div className="scrollable-body" ref={bodyRef}>
        <table className={`universal-table ${className}`}>
          <tbody>
            {paginatedData.map((row, idx) => (
              <tr
                key={idx}
                onClick={() => onRowClick && onRowClick(row)}
                className={row.className ? row.className : ''}
              >
                {renderRow(row)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination-controls">
        <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="pagination-button">
          Prev
        </button>
        {pageNumbers.map((num) => (
          <button
            key={num}
            onClick={() => goToPage(num)}
            disabled={num === currentPage}
            className={`pagination-button${num === currentPage ? ' active' : ''}`}
          >
            {num}
          </button>
        ))}
        {totalPages > 5 && currentPage < totalPages - 2 && (
          <span className="pagination-ellipsis">...</span>
        )}
        <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="pagination-button">
          Next
        </button>
      </div>
    </div>
  );
};

export default Table;
